import { Dialog, Button } from "@mui/material";
import { CheckCircle, X, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { isGoogleEmailForAddress, isLikelyValidEmail } from "../../utils/emailProvider";

const ResetLinkSentModal = ({ open, setOpen, email }) => {
  const [isGoogleEmail, setIsGoogleEmail] = useState(false);
  const [isCheckingProvider, setIsCheckingProvider] = useState(false);

  // Gmail search URL for emails from hello@twilanswer.com in the last 24 hours
  const gmailSearchUrl = `https://mail.google.com/mail/u/0/#search/from:hello@twilanswer.com+newer_than:1d`;

  // Check if email is Google-based when email changes
  useEffect(() => {
    if (!email) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    if (!isLikelyValidEmail(email)) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    setIsCheckingProvider(true);
    const handle = setTimeout(async () => {
      try {
        const result = await isGoogleEmailForAddress(email);
        setIsGoogleEmail(result);
      } catch {
        setIsGoogleEmail(false);
      } finally {
        setIsCheckingProvider(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [email]);

  const handleOpenGmail = () => {
    window.open(gmailSearchUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        style: { backgroundColor: "rgba(0,0,0,0.25)" },
      }}
    >
    {/* <X className="absolute top-4 right-4 cursor-pointer text-gray-500 hover:text-gray-700" size={20} onClick={() => setOpen(false)} /> */}
      <div className="px-6 py-10 flex justify-center">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm text-center px-6 py-8">
          {/* Success Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="text-green-600" size={26} />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Check your email
          </h2>

          {/* Description */}
          <p className="text-sm text-gray-500 mb-5">
            We’ve sent a password reset link to <br />
            <span className="font-medium text-gray-800">{email}</span>
          </p>

          {/* Open Gmail (Conditional) */}
          {isGoogleEmail && (
            <Button
              fullWidth
              onClick={handleOpenGmail}
              startIcon={<ExternalLink size={16} />}
              sx={{
                textTransform: "none",
                backgroundColor: "#16a34a",
                color: "#fff",
                borderRadius: "10px",
                py: "10px",
                mb: "12px",
                "&:hover": {
                  backgroundColor: "#15803d",
                },
              }}
            >
              Open Gmail
            </Button>
          )}

          {/* Helper Text */}
          <p className="text-xs text-gray-400 mb-4">
            Didn’t receive the email? Check your spam folder or try again.
          </p>

          {/* Try Different Email */}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              py: "10px",
              mb: "10px",
              borderColor: "#e5e7eb",
              color: "#374151",
            }}
          >
            Try different email
          </Button>

          {/* Back to Sign In */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: "10px",
              py: "10px",
            }}
          >
            Back
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ResetLinkSentModal;
